import { makeStyles } from '@material-ui/styles'
import { useCallback, useMemo, useState } from 'react'
import TenantPicklist from 'account/components/userManagement/tenants/TenantPicklist'
import { generateApiErrorPayload } from 'api-client/helpers'
import { changeTenantVirtualMachine } from 'app/plugins/openstack/components/vms/actions'
import Alert from 'core/components/Alert'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import ShowActionState, { ActionStateResult } from 'core/components/ShowActionState'
import Button from 'core/elements/button'
import Modal from 'core/elements/modal'
import Text from 'core/elements/Text'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'

/**
 * Dialog for changing the tenant (project) ownership of one or more VMs.
 * Transfers VM instance, attached volumes, and Neutron ports to the target tenant.
 * Supports batch operations — fires parallel API calls for all selected VMs.
 * Shows per-VM success/failure status using ShowActionState.
 *
 * @see PCD-8255
 */
// FR-001: Dialog accepts array of VMs and supports batch operations
const ChangeTenantDialog = ({ rows: vms, onClose }) => {
  const classes = useStyles()

  const [tenantId, setTenantId] = useState(null)
  const [partialSuccess, setPartialSuccess] = useState(false)

  const { updating, bulkUpdate, error } = useUpdateAction(changeTenantVirtualMachine)

  // FR-002: Collect unique current tenant IDs to disable in picker
  const currentTenantIds = useMemo(() => [...new Set(vms?.map((vm) => vm.tenant_id))], [vms])

  // Build id→name map for results display
  const idToNameMap = useMemo(() => {
    return vms?.reduce(
      (acc, vm) => {
        acc[vm.id] = vm.name || vm.id
        return acc
      },
      {} as Record<string, string>,
    )
  }, [vms])

  const [bulkResults, setBulkResults] = useState<Array<ActionStateResult>>(
    vms?.map((vm) => ({
      name: vm.name || vm.id,
      status: '' as const,
      actionName: '',
    })),
  )

  // FR-003: Fire parallel API calls for all selected VMs via bulkUpdate
  const handleSubmit = useCallback(async () => {
    // Set initial in-progress state
    const initialResults: ActionStateResult[] = vms?.map((vm) => ({
      name: vm.name || vm.id,
      status: 'In Progress',
      actionName: 'Changing Tenant',
    }))
    setBulkResults(initialResults)

    // FR-004: Request body shape
    const body = {
      changeTenant: {
        project_id: tenantId,
      },
    }

    const items = vms.map((vm) => ({ id: vm.id, body }))
    const results = await bulkUpdate(items)

    const toActionStateResult = (res: any): ActionStateResult => {
      const resId = res?.params?.id
      const name = idToNameMap[resId] ?? resId ?? 'Unknown'
      const errorPayload = res?.success
        ? null
        : generateApiErrorPayload(res?.error, '', 'Failed to change tenant')
      return {
        name,
        status: res?.success ? 'Success' : 'Failed',
        actionName: res?.success ? 'Tenant Changed' : 'Failed',
        error: res?.success ? null : errorPayload?.message,
      }
    }

    setBulkResults(results?.map(toActionStateResult))

    // FR-005: On all-success, close and trigger list reload
    if (results?.every((res) => res?.success)) {
      onClose(true)
    }

    // On partial success, set flag to show Finish button instead of Cancel/Confirm
    const partialSuccess = results?.some((res) => res?.success)
    setPartialSuccess(partialSuccess)
  }, [vms, tenantId, bulkUpdate, idToNameMap, onClose])

  const handleClose = useCallback(
    (success?: boolean) => {
      setBulkResults([])
      onClose(partialSuccess ? true : success)
    },
    [onClose, partialSuccess],
  )

  const showResults = useMemo(() => bulkResults?.some((r) => !!r?.status), [bulkResults])

  return (
    <Modal
      panel="drawer"
      open
      onClose={() => handleClose()}
      title="Change Tenant"
      maxWidth={600}
      footer={
        showResults ? (
          <Button variant="secondary" onClick={() => handleClose()}>
            Finish
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={() => handleClose()}>
              Cancel
            </Button>
            <Button disabled={!tenantId || updating} onClick={handleSubmit}>
              Confirm
            </Button>
          </>
        )
      }
    >
      <div className={classes.container}>
        {/* FR-007: Info alert about what gets transferred */}
        <Text variant="body2" className={classes.infoText}>
          <FontAwesomeIcon size="lg">arrow-right-arrow-left</FontAwesomeIcon>
          Selected VM(s) and their attached volumes and network ports will be moved to the target
          tenant.
        </Text>
        {/* Error alert */}
        {error?.message && <Alert variant="error" message={error?.message} />}
        {!showResults && (
          <TenantPicklist
            name="tenant"
            label="Target Tenant"
            compact={false}
            excludeItems={currentTenantIds as string[]}
            value={tenantId}
            onChange={(value) => setTenantId(value)}
          />
        )}
        <Text variant="caption1">{'Selected VM(s):'}</Text>
        <ShowActionState results={bulkResults} />
      </div>
    </Modal>
  )
}

export default ChangeTenantDialog

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'grid',
    gridAutoFlow: 'columns',
    gap: theme.spacing(3),
  },
  infoText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
}))
